import { useAtom } from "jotai";
import toast from "react-hot-toast";
import { formatCurrency } from "utils/formatters";
import CrmDashboardSkeleton from "components/skeletons/CrmDashboardSkeleton";
import {Helmet} from "react-helmet";
import axiosInstance from "helpers/api";
import { useCookies } from "react-cookie";
import { Col, Row, Badge, Card, Button, ProgressBar } from "react-bootstrap";
import { useEffect, useState } from "react";
// import "./MobileNavFix"; // Import mobile navigation fix for small screens
import Backdrop from "components/common/Backdrop";
import { user_info } from "jotai/UserJotaiInstance";
import LeadSources from "components/list-items/LeadSources";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faHandshake, 
  faUser, 
  faRobot,
  faChartLine,
  faBrain,
  faDollarSign,
  faPhone,
  faComments,
  faMapMarkerAlt,
  faCalendarAlt,
  faArrowTrendUp,
  faArrowTrendDown,
  faClock,
  faUserTie,
  faTachometerAlt,
  faWaveSquare,
  faLightbulb,
  faFire,
  faThumbsUp,
  faBullseye
} from "@fortawesome/free-solid-svg-icons";
import './Crm.css';
import './DashboardResponsive.css';
import { UserInfo } from "interfaces/user";

// TypeScript interfaces for AI chat
interface ChatMessage {
  type: 'user' | 'ai';
  message: string;
  timestamp: string;
  isTyping?: boolean;
  isError?: boolean;
  context?: any;
}

const Crm = () => {
  const [leads, setLeads] = useState([]);
  const [deals, setDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Start with loading state true
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cookie, setCookie, removeCookie] = useCookies([
    "isLoggedInKIQ",
    "stripeIdpKey",
  ]);

  useEffect(() => {
    removeCookie("stripeIdpKey", { path: "/" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeadData = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get("/dashboard/leads");
      
      // Add proper validation for response structure
      if (response && response.data) {
        // Our API returns { success: true, leads: [...], count: N }
        setLeads(response.data.leads || response.data.data || response.data || []);
      } else {
        console.warn('No data received from leads API');
        setLeads([]);
      }
    } catch (error: any) {
      console.error('Error fetching lead data:', error);
      setLeads([]);
      
      // Only show toast if we have error details
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to fetch lead data');
      }
    }
  };

  const fetchDealData = async () => {
    try {
      const response = await axiosInstance.get("/dashboard/deals");
      
      // Add proper validation for response structure  
      if (response && response.data) {
        // Our API returns { success: true, deals: [...], count: N }
        setDeals(response.data.deals || response.data.data || response.data || []);
      } else {
        console.warn('No data received from deals API');
        setDeals([]);
      }
    } catch (error: any) {
      console.error('Error fetching deal data:', error);
      setDeals([]);
      
      // Only show toast if we have error details
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to fetch deal data');
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        fetchLeadData(),
        fetchDealData()
      ]);
      setIsLoading(false);
    };
    fetchData();
  }, []);

  const totalPrice = deals && Array.isArray(deals) 
    ? deals.reduce((sum, item: any) => {
        const price = typeof item?.deal_price === 'number' ? item.deal_price : 0;
        return sum + price;
      }, 0)
    : 0;

  const [userInfo] = useAtom<UserInfo>(user_info);
  const [showCrm, setShowCrm] = useState(true);
  const isAuthorized = userInfo?.payment_status === "Cancelled" ? false : true;

  useEffect(() => {
    if (!isAuthorized) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList") {
            const backdropExists = document.getElementById("kairos-backdrop");
            if (!backdropExists) {
              setShowCrm(false);
            }
          }
        });
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
      return () => observer.disconnect();
    }
  }, [isAuthorized]);

  // Mock data for enhanced dashboard - Replace with real API calls
  const mockStats = {
    avgResponseTime: "2.3 hours",
    conversionRate: "24%",
    activeSmsCampaigns: 3,
    monthlyRoi: "156%",
    appointmentsToday: 7,
    hotLeads: 12
  };

  // Personalized greeting logic
  const getPersonalizedGreeting = () => {
    const hour = new Date().getHours();
    const firstName = userInfo?.user_first_name?.split(' ')[0] || userInfo?.user_email?.split('@')[0] || 'there';
    
    let timeOfDay = '';
    if (hour < 12) timeOfDay = 'Good morning';
    else if (hour < 17) timeOfDay = 'Good afternoon';
    else timeOfDay = 'Good evening';
    
    const greetings = [
      `${timeOfDay}, ${firstName}! Ready to close some deals today?`,
      `Hi ${firstName}! Let's maximize your lead potential today.`,
      `${timeOfDay}, ${firstName}! Your AI assistant is ready to help.`,
      `Welcome back, ${firstName}! Let's grow your business together.`,
      `${timeOfDay}, ${firstName}! Time to turn leads into closings.`
    ];
    
    return greetings[Math.floor(Math.random() * greetings.length)];
  };

  // AI Chatbot state
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [chatbotMinimized, setChatbotMinimized] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      type: 'ai' as const,
      message: `Hi ${userInfo?.user_first_name?.split(' ')[0] || 'there'}! I'm your KairosIQ AI assistant. I can help you with lead management, market insights, and workflow automation. What would you like to work on?`,
      timestamp: new Date().toISOString()
    }
  ]);
  const [chatInput, setChatInput] = useState('');

  // AI Chatbot handlers with real AWS Bedrock Claude integration
  const handleChatSubmit = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage: ChatMessage = {
      type: 'user' as const,
      message: chatInput,
      timestamp: new Date().toISOString()
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    const currentInput = chatInput;
    setChatInput('');
    
    // Add typing indicator
    const typingMessage: ChatMessage = {
      type: 'ai' as const,
      message: 'KairosIQ AI is thinking...',
      timestamp: new Date().toISOString(),
      isTyping: true
    };
    setChatMessages(prev => [...prev, typingMessage]);
    
    try {
      // Get conversation history for context
      const conversationHistory = chatMessages
        .filter(msg => !msg.isTyping && !msg.isError)
        .slice(-6) // Keep last 6 messages for context
        .map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.message
        }));

      // Call the real AWS Bedrock Claude API
      const response = await axiosInstance.post('/ai-chatbot/chat', {
        message: currentInput,
        conversation_history: conversationHistory
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });

      const aiResponse = response.data?.data?.response || "I'm here to help with your real estate business!";
      
      // Remove typing indicator and add real AI response
      setChatMessages(prev => {
        const filtered = prev.filter(msg => !msg.isTyping);
        return [...filtered, {
          type: 'ai' as const,
          message: aiResponse,
          timestamp: new Date().toISOString(),
          context: response.data?.data?.context || {}
        }];
      });
      
    } catch (error: any) {
      console.error('🚨 Dashboard Chat Bot Error:', error);
      console.error('🚨 Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : null,
        response: error.response?.data || 'No response data',
        status: error.response?.status || 'No status'
      });
      
      // Show the actual error instead of hiding it
      setChatMessages(prev => {
        const filtered = prev.filter(msg => !msg.isTyping);
        return [...filtered, {
          type: 'ai' as const,
          message: `❌ Connection Error: ${error instanceof Error ? error.message : 'Unknown error'}. Check console for details.`,
          timestamp: new Date().toISOString(),
          isError: true,
          context: { mode: 'error', error: error instanceof Error ? error.message : 'Unknown error' }
        }];
      });
    }
  };

  // Intelligent mock response generator for demo/fallback mode (keeping for potential future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const generateIntelligentMockResponse = (input: string, userInfo: any, leads: any[], deals: any[], totalPrice: number) => {
    const firstName = userInfo?.name?.split(' ')[0] || 'there';
    const lowerInput = input.toLowerCase();
    
    // Lead-related responses
    if (lowerInput.includes('lead') || lowerInput.includes('prospect')) {
      const hotLeadsCount = Math.floor((leads?.length || 0) * 0.3);
      return `Great question about leads, ${firstName}! You have ${leads?.length || 0} active leads, with ${hotLeadsCount} showing high engagement. I recommend focusing on the leads that opened your recent emails or responded to SMS campaigns. Would you like me to help prioritize your follow-up strategy?`;
    }
    
    // Deal-related responses
    if (lowerInput.includes('deal') || lowerInput.includes('closing') || lowerInput.includes('sale')) {
      return `Looking at your pipeline, ${firstName}, you have ${deals?.length || 0} active deals worth ${formatCurrency(totalPrice)}. Based on typical market trends, I'd estimate 2-3 of these should close this month. Focus on deals in the "Under Contract" stage and ensure all contingencies are being met on schedule.`;
    }
    
    // Market-related responses
    if (lowerInput.includes('market') || lowerInput.includes('price') || lowerInput.includes('trend')) {
      return `The current market shows strong buyer activity, ${firstName}. Interest rates have stabilized, and inventory is improving in your area. This is a great time to push listings and generate CMAs for prospects. Properties priced correctly are moving within 2-3 weeks.`;
    }
    
    // CMA or analysis requests
    if (lowerInput.includes('cma') || lowerInput.includes('analysis') || lowerInput.includes('estimate')) {
      return `I can definitely help with comparative market analysis! For accurate CMAs, I'd recommend using our Smart CMA tool which analyzes recent comps, market trends, and neighborhood data. Would you like me to walk you through generating a CMA for a specific property?`;
    }
    
    // SMS or marketing related
    if (lowerInput.includes('sms') || lowerInput.includes('text') || lowerInput.includes('campaign') || lowerInput.includes('marketing')) {
      return `SMS campaigns are performing well in today's market! I suggest sending personalized follow-ups to leads who haven't responded in 5-7 days. Market update messages and new listing alerts typically get the best engagement rates. Want me to suggest some message templates?`;
    }
    
    // General productivity or workflow
    if (lowerInput.includes('help') || lowerInput.includes('today') || lowerInput.includes('priority') || lowerInput.includes('task')) {
      return `Here's what I'd prioritize today, ${firstName}: 1) Follow up with your ${Math.floor((leads?.length || 0) * 0.2)} most engaged leads, 2) Review deals approaching key milestone dates, 3) Send market updates to your sphere. Your dashboard shows strong activity - keep up the momentum!`;
    }
    
    // Greeting responses
    if (lowerInput.includes('hello') || lowerInput.includes('hi') || lowerInput.includes('hey')) {
      return `Hello ${firstName}! I'm here to help optimize your real estate business. I can assist with lead prioritization, deal management, market insights, and workflow automation. What would you like to focus on today?`;
    }
    
    // Default intelligent response
    return `That's a great question, ${firstName}! While I'm temporarily working in demo mode, I can see you have ${leads?.length || 0} leads and ${deals?.length || 0} deals in your pipeline. Based on your dashboard data, I'd recommend focusing on lead nurturing and deal progression. How can I help you prioritize your next steps?`;
  };

  const handleSuggestion = (suggestion: string) => {
    setChatInput(suggestion);
    setTimeout(() => handleChatSubmit(), 100);
  };



  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>KairosIQ.ai - Real Estate Intelligence Dashboard</title>
      </Helmet>
      {(isAuthorized || showCrm) && (
        isLoading ? <CrmDashboardSkeleton /> : (
        <div className="enhanced-crm-dashboard">
          {/* Enhanced Header with Quick Actions */}
          <div className="dashboard-header-enhanced">
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div>
                <div className="d-flex align-items-center gap-3 mb-2">
                  <h1 className="dashboard-title-enhanced m-0">
                    <FontAwesomeIcon icon={faTachometerAlt} className="me-3" style={{ color: '#00d4ff', filter: 'drop-shadow(0 0 12px rgba(0, 212, 255, 0.6))' }} />
                    Real Estate Intelligence Hub
                  </h1>
                  <div className="d-flex gap-2">
                    <Badge className="ai-badge-enhanced">
                    <FontAwesomeIcon icon={faBrain} className="me-1" />
                    AI-POWERED
                  </Badge>
                  <Badge className="production-badge">
                    <FontAwesomeIcon icon={faFire} className="me-1" />
                    LIVE
                  </Badge>
                  </div>
                </div>
                <p className="dashboard-subtitle-enhanced mb-2">
                  <FontAwesomeIcon icon={faWaveSquare} className="me-2" style={{ color: '#00d4ff' }} />
                  Advanced workflow orchestration with ML-powered insights
                </p>
                <div className="ai-greeting-card">
                  <div className="d-flex align-items-center">
                    <div className="ai-avatar me-3">
                      <FontAwesomeIcon icon={faRobot} />
                    </div>
                    <div className="greeting-text">
                      <p className="mb-0">{getPersonalizedGreeting()}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="quick-actions-panel">
                <Button variant="primary" className="action-btn me-2">
                  <FontAwesomeIcon icon={faPhone} className="me-2" />
                  SMS Campaign
                </Button>
                <Button variant="outline-primary" className="action-btn">
                  <FontAwesomeIcon icon={faLightbulb} className="me-2" />
                  AI Insights
                </Button>
              </div>
            </div>
          </div>

          {/* Enhanced Metrics Grid */}
          <Row className="g-4 mb-4">
            {/* Primary Metrics */}
            <Col xl={3} lg={4} md={6}>
              <Card className="metric-card-enhanced leads-card">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="metric-icon-container leads-icon">
                      <FontAwesomeIcon icon={faUserTie} size="lg" />
                    </div>
                    <div className="metric-status active-status">
                      <FontAwesomeIcon icon={faArrowTrendUp} className="me-1" />
                      <span>Growing</span>
                    </div>
                  </div>
                  <div className="metric-value-enhanced">{leads?.length || 0}</div>
                  <div className="metric-label-enhanced">Active Leads</div>
                  <div className="metric-detail">
                                         <FontAwesomeIcon icon={faBullseye} className="me-2" />
                    {mockStats.hotLeads} hot prospects
                  </div>
                  <ProgressBar now={75} className="metric-progress mt-2" />
                </Card.Body>
              </Card>
            </Col>

            <Col xl={3} lg={4} md={6}>
              <Card className="metric-card-enhanced deals-card">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="metric-icon-container deals-icon">
                      <FontAwesomeIcon icon={faHandshake} size="lg" />
                    </div>
                    <div className="metric-status success-status">
                      <FontAwesomeIcon icon={faThumbsUp} className="me-1" />
                      <span>Excellent</span>
                    </div>
                  </div>
                  <div className="metric-value-enhanced">{deals?.length || 0}</div>
                  <div className="metric-label-enhanced">Active Deals</div>
                  <div className="metric-detail">
                    <FontAwesomeIcon icon={faDollarSign} className="me-2" />
                    {formatCurrency(totalPrice)} total value
                  </div>
                  <ProgressBar now={60} className="metric-progress mt-2" />
                </Card.Body>
              </Card>
            </Col>

            <Col xl={3} lg={4} md={6}>
              <Card className="metric-card-enhanced conversion-card">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="metric-icon-container conversion-icon">
                      <FontAwesomeIcon icon={faChartLine} size="lg" />
                    </div>
                    <div className="metric-status warning-status">
                      <FontAwesomeIcon icon={faClock} className="me-1" />
                      <span>Tracking</span>
                    </div>
                  </div>
                  <div className="metric-value-enhanced">{mockStats.conversionRate}</div>
                  <div className="metric-label-enhanced">Conversion Rate</div>
                  <div className="metric-detail">
                    <FontAwesomeIcon icon={faComments} className="me-2" />
                    {mockStats.avgResponseTime} avg response
                  </div>
                  <ProgressBar now={24} className="metric-progress mt-2" />
                </Card.Body>
              </Card>
            </Col>

            <Col xl={3} lg={4} md={6}>
              <Card className="metric-card-enhanced communication-card">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="metric-icon-container communication-icon">
                      <FontAwesomeIcon icon={faPhone} size="lg" />
                    </div>
                    <div className="metric-status active-status">
                      <FontAwesomeIcon icon={faRobot} className="me-1" />
                      <span>Automated</span>
                    </div>
                  </div>
                  <div className="metric-value-enhanced">{mockStats.activeSmsCampaigns}</div>
                  <div className="metric-label-enhanced">SMS Campaigns</div>
                  <div className="metric-detail">
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                    {mockStats.appointmentsToday} appointments today
                  </div>
                  <ProgressBar now={85} className="metric-progress mt-2" />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Real-Time AI Recommendations */}
          <Row className="g-4 mb-4">
            <Col xl={12}>
              <div 
                className="ai-recommendations-section"
                style={{
                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(217, 119, 6, 0.05) 100%)',
                  borderRadius: '16px',
                  padding: '24px',
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                  backdropFilter: 'blur(15px)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div className="d-flex align-items-center mb-3">
                  <FontAwesomeIcon 
                    icon={faBrain} 
                    style={{ 
                      color: '#f59e0b', 
                      fontSize: '24px',
                      marginRight: '12px',
                      filter: 'drop-shadow(0 0 8px rgba(245, 158, 11, 0.5))',
                      animation: 'pulse 3s infinite'
                    }} 
                  />
                  <h5 
                    className="mb-0 fw-bold"
                    style={{ 
                      color: '#1f2937',
                      textShadow: '0 2px 10px rgba(245, 158, 11, 0.2)'
                    }}
                  >
                    🔥 Real-Time AI Recommendations
                  </h5>
                  <div 
                    className="ms-auto"
                    style={{
                      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.2) 100%)',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      border: '1px solid rgba(245, 158, 11, 0.3)'
                    }}
                  >
                    <span style={{ color: '#f59e0b', fontSize: '12px', fontWeight: 600 }}>
                      LIVE ANALYSIS
                    </span>
                  </div>
                </div>

                <div 
                  className="recommendations-grid"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                    gap: '1rem'
                  }}
                >
                  {/* Priority Action Recommendation */}
                  <div 
                    className="recommendation-card priority"
                    style={{
                      background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '12px',
                      padding: '16px'
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <FontAwesomeIcon 
                        icon={faFire} 
                        style={{ 
                          color: '#ef4444', 
                          fontSize: '16px',
                          marginRight: '12px',
                          marginTop: '2px'
                        }} 
                      />
                      <div style={{ flex: 1 }}>
                        <h6 style={{ color: '#1f2937', fontWeight: 600, marginBottom: '8px' }}>
                          🚨 Priority Action Required
                        </h6>
                        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '8px' }}>
                          {leads?.length === 0 
                            ? "No new leads this week. Focus on lead generation through your highest-converting source: Cold Calling."
                            : (leads?.length || 0) < 5 
                              ? `Low lead volume with ${leads?.length || 0} leads. Prioritize following up within 24 hours to maximize conversions.`
                              : `Great work! ${leads?.length || 0} active leads. Focus on nurturing your pipeline for optimal conversions.`
                          }
                        </p>
                        <span style={{ 
                          color: '#ef4444', 
                          fontSize: '12px', 
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Act within 2 hours
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Lead Source Optimization */}
                  <div 
                    className="recommendation-card optimization"
                    style={{
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '12px',
                      padding: '16px'
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <FontAwesomeIcon 
                        icon={faChartLine} 
                        style={{ 
                          color: '#3b82f6', 
                          fontSize: '16px',
                          marginRight: '12px',
                          marginTop: '2px'
                        }} 
                      />
                      <div style={{ flex: 1 }}>
                        <h6 style={{ color: '#1f2937', fontWeight: 600, marginBottom: '8px' }}>
                          📈 Lead Source Optimization
                        </h6>
                        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '8px' }}>
                          Realtor.com has 35% conversion rate vs Cold Calling at 16%. 
                          Increase Realtor.com investment by 40% and reduce cold calling by 20%.
                        </p>
                        <span style={{ 
                          color: '#3b82f6', 
                          fontSize: '12px', 
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Potential +25% ROI
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* AI Workflow Suggestion */}
                  <div 
                    className="recommendation-card workflow"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      borderRadius: '12px',
                      padding: '16px'
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <FontAwesomeIcon 
                        icon={faRobot} 
                        style={{ 
                          color: '#10b981', 
                          fontSize: '16px',
                          marginRight: '12px',
                          marginTop: '2px'
                        }} 
                      />
                      <div style={{ flex: 1 }}>
                        <h6 style={{ color: '#1f2937', fontWeight: 600, marginBottom: '8px' }}>
                          🤖 Smart Workflow Suggestion
                        </h6>
                        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '8px' }}>
                          Enable AI Auto-Nurture for leads older than 14 days. 
                          This could automatically engage {Math.floor((leads?.length || 0) * 0.3)} leads with personalized follow-ups.
                        </p>
                        <span style={{ 
                          color: '#10b981', 
                          fontSize: '12px', 
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Save 5 hours/week
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Market Intelligence */}
                  <div 
                    className="recommendation-card intelligence"
                    style={{
                      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '12px',
                      padding: '16px'
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <FontAwesomeIcon 
                        icon={faLightbulb} 
                        style={{ 
                          color: '#8b5cf6', 
                          fontSize: '16px',
                          marginRight: '12px',
                          marginTop: '2px'
                        }} 
                      />
                      <div style={{ flex: 1 }}>
                        <h6 style={{ color: '#1f2937', fontWeight: 600, marginBottom: '8px' }}>
                          🏠 Market Intelligence
                        </h6>
                        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '8px' }}>
                          Phoenix market shows 12% increase in buyer activity. 
                          Focus on luxury listings $500K+ for next 30 days.
                        </p>
                        <span style={{ 
                          color: '#8b5cf6', 
                          fontSize: '12px', 
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Market opportunity
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Center */}
                <div 
                  className="ai-action-center mt-3"
                  style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'center',
                    flexWrap: 'wrap'
                  }}
                >
                  <Button
                    variant="outline-warning"
                    size="sm"
                    style={{
                      background: 'rgba(245, 158, 11, 0.1)',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      color: '#f59e0b',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '6px 16px'
                    }}
                  >
                    📊 View Detailed Analysis
                  </Button>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    style={{
                      background: 'rgba(245, 158, 11, 0.1)',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      color: '#f59e0b',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '6px 16px'
                    }}
                  >
                    🤖 Setup AI Auto-Actions
                  </Button>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    style={{
                      background: 'rgba(245, 158, 11, 0.1)',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      color: '#f59e0b',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '6px 16px'
                    }}
                  >
                    📈 Optimize Budget Allocation
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Workflow Orchestration Panel */}
          <Row className="g-4 mb-4">
            <Col xl={8}>
              <Card className="workflow-panel-enhanced">
                <Card.Header className="workflow-header">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h5 className="mb-1">
                        <FontAwesomeIcon icon={faWaveSquare} className="me-2" style={{ color: '#00d4ff' }} />
                        Lead Workflow Orchestration
                      </h5>
                      <p className="text-muted mb-0">AI-powered automation pipeline</p>
                    </div>
                    <Button variant="outline-primary" size="sm">
                      <FontAwesomeIcon icon={faLightbulb} className="me-2" />
                      Optimize
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-4">
                  <div className="workflow-stages">
                    <div className="stage-item active">
                      <div className="stage-icon">
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                      <div className="stage-content">
                        <h6>Lead Capture</h6>
                        <p className="text-muted">AI-powered lead qualification</p>
                        <div className="stage-metrics">
                          <span className="metric-badge">+{leads?.length || 0} this week</span>
                        </div>
                      </div>
                    </div>
                    <div className="stage-connector"></div>
                    <div className="stage-item active">
                      <div className="stage-icon">
                        <FontAwesomeIcon icon={faComments} />
                      </div>
                      <div className="stage-content">
                        <h6>AI Engagement</h6>
                        <p className="text-muted">Automated SMS & Email sequences</p>
                        <div className="stage-metrics">
                          <span className="metric-badge">{mockStats.avgResponseTime} response</span>
                        </div>
                      </div>
                    </div>
                    <div className="stage-connector"></div>
                    <div className="stage-item">
                      <div className="stage-icon">
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                      </div>
                      <div className="stage-content">
                        <h6>CMA Generation</h6>
                        <p className="text-muted">Advanced ML predictions</p>
                        <div className="stage-metrics">
                          <span className="metric-badge">92% accuracy</span>
                        </div>
                      </div>
                    </div>
                    <div className="stage-connector"></div>
                    <div className="stage-item">
                      <div className="stage-icon">
                        <FontAwesomeIcon icon={faHandshake} />
                      </div>
                      <div className="stage-content">
                        <h6>Deal Closing</h6>
                        <p className="text-muted">Automated appointment scheduling</p>
                        <div className="stage-metrics">
                          <span className="metric-badge">{mockStats.conversionRate} conversion</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xl={4}>
              <Card className="ai-insights-panel">
                <Card.Header>
                  <h5 className="mb-0">
                    <FontAwesomeIcon icon={faBrain} className="me-2" style={{ color: '#00d4ff' }} />
                    AI Insights
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="insight-item">
                    <div className="insight-icon">
                      <FontAwesomeIcon icon={faFire} />
                    </div>
                    <div className="insight-content">
                      <h6>Hot Market Alert</h6>
                      <p className="text-muted">Scottsdale market up 12% this month</p>
                    </div>
                  </div>
                  <div className="insight-item">
                    <div className="insight-icon">
                                             <FontAwesomeIcon icon={faBullseye} />
                    </div>
                    <div className="insight-content">
                      <h6>Opportunity Detected</h6>
                      <p className="text-muted">3 leads ready for CMA analysis</p>
                    </div>
                  </div>
                  <div className="insight-item">
                    <div className="insight-icon">
                      <FontAwesomeIcon icon={faPhone} />
                    </div>
                    <div className="insight-content">
                      <h6>Twilio Integration</h6>
                      <p className="text-muted">Phone numbers active & optimized</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* AI Lead Source Intelligence */}
          <Row className="g-4">
            <Col xl={12}>
              <Card className="leads-overview-card">
                <Card.Header>
                  <h5 className="mb-0">
                    <FontAwesomeIcon icon={faBrain} className="me-2" style={{ color: '#00d4ff' }} />
                    AI Lead Source Intelligence
                  </h5>
                  <small className="text-muted">Neural network-powered channel performance analysis</small>
                </Card.Header>
                <Card.Body>
                  <LeadSources leads={leads || []} />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* AI Chatbot Assistant */}
          <div className={`ai-chatbot-container ${chatbotOpen ? 'open' : ''} ${chatbotMinimized ? 'minimized' : ''}`}>
            {!chatbotOpen && (
              <div className="chatbot-toggle" onClick={() => setChatbotOpen(true)}>
                <div className="chatbot-toggle-content">
                  <FontAwesomeIcon icon={faComments} className="me-2" />
                  <span>AI Assistant</span>
                  <div className="ai-pulse-indicator"></div>
                </div>
              </div>
            )}
            
            {chatbotOpen && (
              <div className="chatbot-panel">
                <div className="chatbot-header">
                  <div className="d-flex align-items-center">
                    <div className="ai-avatar-small me-2">
                      <FontAwesomeIcon icon={faBrain} />
                    </div>
                    <div>
                      <h6 className="mb-0">KairosIQ AI Assistant</h6>
                      <small className="text-muted">Lead & Market Intelligence</small>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="p-0 text-muted"
                      onClick={() => setChatbotMinimized(!chatbotMinimized)}
                      title={chatbotMinimized ? "Expand" : "Minimize"}
                    >
                      <FontAwesomeIcon icon={chatbotMinimized ? faArrowTrendUp : faArrowTrendDown} />
                    </Button>
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="p-0 text-muted"
                      onClick={() => setChatbotOpen(false)}
                      title="Close"
                    >
                      <FontAwesomeIcon icon={faArrowTrendUp} style={{ transform: 'rotate(45deg)' }} />
                    </Button>
                  </div>
                </div>
                
                <div className="chatbot-messages">
                  {chatMessages.map((msg, index) => (
                    <div key={index} className={`message ${msg.type}`}>
                      {msg.type === 'ai' && (
                        <div className="ai-avatar-tiny me-2">
                          <FontAwesomeIcon icon={faBrain} />
                        </div>
                      )}
                      <div className="message-content">
                        <p className="mb-1">{msg.message}</p>
                        <small className="timestamp">{new Date(msg.timestamp).toLocaleTimeString()}</small>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="chatbot-input">
                  <div className="d-flex">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ask about your leads, deals, or market insights..."
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleChatSubmit();
                        }
                      }}
                    />
                    <Button 
                      variant="primary" 
                      className="ms-2"
                      onClick={handleChatSubmit}
                      disabled={!chatInput.trim()}
                    >
                      <FontAwesomeIcon icon={faComments} />
                    </Button>
                  </div>
                </div>
                
                <div className="chatbot-suggestions">
                  <div className="d-flex gap-2 flex-wrap">
                    <button 
                      className="suggestion-chip"
                      onClick={() => handleSuggestion("Show me my hottest leads")}
                    >
                      🔥 Hot Leads
                    </button>
                    <button 
                      className="suggestion-chip"
                      onClick={() => handleSuggestion("Generate CMA for new prospect")}
                    >
                      📊 Generate CMA
                    </button>
                    <button 
                      className="suggestion-chip"
                      onClick={() => handleSuggestion("Send follow-up SMS campaign")}
                    >
                      📱 SMS Campaign
                    </button>
                    <button 
                      className="suggestion-chip"
                      onClick={() => handleSuggestion("Market insights for Scottsdale")}
                    >
                      🏘️ Market Data
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        )
      )}
      {!isAuthorized && <Backdrop />}
    </>
  );
};

export default Crm;
